import { Link } from 'react-router-dom';
import { ClockIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useUIStore from '../../store/uiStore';

export default function DocumentHistory({ documents }) {
  const { removeRecentDocument } = useUIStore();

  if (!documents || documents.length === 0) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'bg-status-success';
      case 'failed':
        return 'bg-status-error';
      default:
        return 'bg-text-muted';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="mt-4 pt-4 border-t border-theme-primary">
      <div className="flex items-center gap-2 px-3 mb-3">
        <ClockIcon className="w-4 h-4 text-theme-muted" />
        <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
          Recent
        </p>
      </div>

      <div className="space-y-1">
        {documents.slice(0, 5).map((doc) => (
          <div
            key={doc.id}
            className="group relative flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 hover:bg-theme-tertiary"
          >
            <Link
              to={`/documents/${doc.slug || doc.id}`}
              className="flex items-center gap-3 flex-1 min-w-0"
            >
              {/* Thumbnail or Icon */}
              <div className="w-8 h-8 rounded-lg bg-theme-elevated border border-theme-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                {doc.thumbnail ? (
                  <img
                    src={doc.thumbnail}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <DocumentTextIcon
                  className="w-4 h-4 text-theme-muted"
                  style={{ display: doc.thumbnail ? 'none' : 'block' }}
                />
              </div>

              {/* Document Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-theme-primary truncate group-hover:text-brand-orange transition-colors">
                  {doc.title}
                </p>
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(doc.status)}`} />
                  <span className="text-xs text-theme-muted">
                    {formatTimeAgo(doc.accessedAt)}
                  </span>
                </div>
              </div>
            </Link>

            {/* Remove Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                removeRecentDocument(doc.id);
              }}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-theme-elevated transition-all"
              title="Remove from recent"
            >
              <XMarkIcon className="w-4 h-4 text-theme-muted hover:text-theme-primary" />
            </button>
          </div>
        ))}
      </div>

      {documents.length > 5 && (
        <Link
          to="/documents"
          className="flex items-center justify-center gap-2 mt-2 px-3 py-2 text-xs font-medium text-brand-orange hover:bg-theme-tertiary rounded-xl transition-colors"
        >
          View all documents
        </Link>
      )}
    </div>
  );
}
